/**
 * =============================================================================
 * Hook: useAffiliateProfile
 * Versão: 2.0.0 (Server-side hardening)
 *
 * Mudanças v2.0.0:
 *   - Update agora vai SEMPRE pela RPC `update_affiliate_profile_safe`,
 *     que aplica whitelist estrita no servidor (bloqueia mexer em status,
 *     saldo, tenant_id, etc).
 *   - A leitura ainda usa o SELECT direto em `affiliates` (cobertura RLS:
 *     `affiliate_view_own` permanece com user_id = auth.uid()), trazendo
 *     apenas as colunas editáveis. Saldos NÃO são lidos aqui.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateProfile');

export interface AffiliateProfileRow {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  document_number: string | null;
  document_type: string | null;
  pix_key: string | null;
  pix_key_type: string | null;
  birth_date: string | null;
  personal_slug: string | null;
  avatar_url: string | null;
  /** Comentário público exibido no formulário de indicação. */
  bio_comment: string | null;
}

/**
 * Campos editáveis pelo próprio afiliado.
 *
 * IMPORTANTE: documentos (CPF/CNPJ) são INTENCIONALMENTE imutáveis pelo
 * afiliado para evitar fraudes (trocar documento depois de gerar comissões).
 * Eles aparecem no tipo só para compatibilidade com formulários antigos —
 * são ignorados pelo hook ao montar o payload da RPC.
 *
 * Mudança de status, saldo ou tenant_id NUNCA é aceita: a RPC tem whitelist.
 */
export interface AffiliateProfileUpdate {
  full_name?: string;
  /**
   * Foto de perfil:
   *   - undefined / não enviado: não altera
   *   - null ou '': remove a foto atual
   *   - string com path/URL: define a nova foto
   */
  avatar_url?: string | null;
  phone?: string | null;
  pix_key?: string | null;
  pix_key_type?: string | null;
  /**
   * Comentário público (até 500 caracteres). Mesma semântica do avatar_url:
   * null/'' remove, string preenche, undefined mantém.
   */
  bio_comment?: string | null;
  /** @deprecated ignorado server-side. Mudança de documento exige suporte. */
  document_number?: string | null;
  /** @deprecated ignorado server-side. Mudança de documento exige suporte. */
  document_type?: string | null;
}

export function useAffiliateProfile() {
  const { affiliate, ready } = useAffiliateAuth();

  return useQuery({
    queryKey: ['affiliate-profile', affiliate?.id],
    enabled: ready && !!affiliate?.id,
    queryFn: async (): Promise<AffiliateProfileRow | null> => {
      // Acesso direto a `affiliates` foi bloqueado pelo hardening RLS.
      // A leitura agora vai 100% pela RPC SECURITY DEFINER com whitelist.
      const { data, error } = await supabase.rpc('get_affiliate_profile_safe');

      if (error) {
        log.error('Falha ao carregar perfil via RPC segura', error);
        throw error;
      }
      return (data as unknown as AffiliateProfileRow | null) ?? null;
    },
    staleTime: 30_000,
  });
}

export function useUpdateAffiliateProfile() {
  const { refresh } = useAffiliateAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: AffiliateProfileUpdate) => {
      // Convenção da RPC v2.1.0:
      //   - undefined  → null   → mantém valor atual (COALESCE no servidor)
      //   - null       → ''     → limpa o valor (apenas avatar_url e bio_comment)
      //   - string     → string → grava
      const toClearable = (v: string | null | undefined): string | null => {
        if (v === undefined) return null; // não altera
        if (v === null) return '';        // sinaliza limpeza
        return v;                         // grava
      };

      const { error } = await supabase.rpc('update_affiliate_profile_safe', {
        p_full_name: payload.full_name ?? null,
        p_avatar_url: toClearable(payload.avatar_url),
        p_phone: payload.phone ?? null,
        p_pix_key: payload.pix_key ?? null,
        p_pix_key_type: payload.pix_key_type ?? null,
        p_bio_comment: toClearable(payload.bio_comment),
      } as any);

      if (error) {
        log.error('Falha ao atualizar perfil via RPC segura', error);
        throw error;
      }
    },
    onSuccess: async () => {
      queryClient.invalidateQueries({ queryKey: ['affiliate-profile'] });
      queryClient.invalidateQueries({ queryKey: ['affiliate-home-overview'] });
      // Recarrega o contexto global (nome/avatar usados no header/sidebar).
      await refresh();
    },
  });
}
